import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Copy, Check, Eye, LogOut, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fetchWithAuth = async (url, options = {}) => {
  const token = sessionStorage.getItem('hnc_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
      ...options.headers
    }
  });
};

export const AdminDashboard = ({ onClose, onLogout }) => {
  const [logs, setLogs] = useState([]);
  const [codes, setCodes] = useState([]);
  const [activeTab, setActiveTab] = useState('logs');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCode, setNewCode] = useState({ userType: 'new_member', customCode: '' });
  const [generatedCode, setGeneratedCode] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [showIPRevealModal, setShowIPRevealModal] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [adminKeyInput, setAdminKeyInput] = useState('');
  const [revealedIP, setRevealedIP] = useState(null);
  const [revealError, setRevealError] = useState('');

  useEffect(() => {
    fetchLogs();
    fetchCodes();
  }, []);

  const fetchLogs = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/admin/logs`);
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch logs');
    }
  };

  const fetchCodes = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/admin/codes`);
      if (res.ok) {
        const data = await res.json();
        setCodes(data.codes || []);
      }
    } catch (err) {
      console.error('Failed to fetch codes');
    }
  };

  const handleCreateCode = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/admin/codes`, {
        method: 'POST',
        body: JSON.stringify({
          user_type: newCode.userType,
          code: newCode.customCode || null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setGeneratedCode({ neutral_id: data.neutral_id, code: data.code });
        setNewCode({ userType: 'new_member', customCode: '' });
        fetchCodes();
      }
    } catch (err) {
      alert('Failed to create code');
    }
  };

  const handleToggleCode = async (codeId, active) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/admin/codes/${codeId}?active=${!active}`, {
        method: 'PATCH'
      });
      if (res.ok) fetchCodes();
    } catch (err) {
      alert('Failed to toggle code');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleRevealIP = async () => {
    setRevealError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/admin/logs/reveal-ip`, {
        method: 'POST',
        body: JSON.stringify({ log_id: selectedLogId, admin_key: adminKeyInput })
      });
      
      if (res.ok) {
        const data = await res.json();
        setRevealedIP(data.full_ip);
        setAdminKeyInput('');
      } else {
        setRevealError('Invalid admin key');
      }
    } catch (err) {
      setRevealError('Failed to reveal IP');
    }
  };

  const closeIPRevealModal = () => {
    setShowIPRevealModal(false);
    setSelectedLogId(null);
    setAdminKeyInput('');
    setRevealedIP(null);
    setRevealError('');
  };

  const handleLogout = () => {
    if (onLogout) onLogout();
    onClose();
  };

  const successCount = logs.filter(l => l.success).length;
  const failCount = logs.filter(l => !l.success).length;

  return (
    <motion.div className="admin-dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-left">
            <Shield size={20} className="admin-shield-icon" />
            <h1 className="admin-title">Secure Admin Panel</h1>
          </div>
          <div className="admin-header-actions">
            <button onClick={handleLogout} className="admin-logout" title="Logout"><LogOut size={18} /></button>
            <button onClick={onClose} className="admin-close"><X size={20} /></button>
          </div>
        </div>

        <div className="admin-security-notice">
          <span>🔒 All IPs masked • Session expires in 30 min • Actions logged</span>
        </div>

        <div className="admin-stats">
          <div className="stat-card"><span className="stat-label">Total Entries</span><span className="stat-value">{logs.length}</span></div>
          <div className="stat-card"><span className="stat-label">Successful</span><span className="stat-value success">{successCount}</span></div>
          <div className="stat-card"><span className="stat-label">Failed</span><span className="stat-value failed">{failCount}</span></div>
          <div className="stat-card"><span className="stat-label">Active Codes</span><span className="stat-value">{codes.filter(c => c.active).length}</span></div>
        </div>

        <div className="admin-tabs">
          <button className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}>Access Logs</button>
          <button className={`tab-button ${activeTab === 'codes' ? 'active' : ''}`} onClick={() => setActiveTab('codes')}>Manage Codes</button>
        </div>

        {activeTab === 'logs' && (
          <div className="admin-content">
            <div className="logs-table">
              <table>
                <thead>
                  <tr>
                    <th>Time</th><th>ID</th><th>Status</th><th>Country</th><th>City</th><th>IP (Masked)</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={i}>
                      <td>{new Date(log.accessed_at).toLocaleString()}</td>
                      <td className="neutral-id">{log.neutral_id || '-'}</td>
                      <td><span className={`status-badge ${log.success ? 'success' : 'failed'}`}>{log.success ? 'Success' : 'Failed'}</span></td>
                      <td>{log.country || '-'}</td>
                      <td>{log.city || '-'}</td>
                      <td className="masked-ip">{log.ip_address}</td>
                      <td><button className="reveal-ip-btn" onClick={() => { setSelectedLogId(log.id); setShowIPRevealModal(true); }}><Eye size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'codes' && (
          <div className="admin-content">
            <button className="create-code-button" onClick={() => setShowCreateModal(true)}>Create New Code</button>
            <div className="codes-grid">
              {codes.map((code) => (
                <div key={code.id} className="code-card">
                  <div className="code-header">
                    <span className="code-label">{code.neutral_id}</span>
                    <span className={`code-status ${code.active ? 'active' : 'inactive'}`}>{code.active ? 'Active' : 'Inactive'}</span>
                  </div>
                  <div className="code-type">{code.user_type}</div>
                  <div className="code-actions">
                    <button onClick={() => handleToggleCode(code.id, code.active)} className="toggle-button">{code.active ? 'Deactivate' : 'Activate'}</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => { setShowCreateModal(false); setGeneratedCode(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {!generatedCode ? (
              <>
                <h2 className="modal-title">Create New Access Code</h2>
                <p className="modal-security-note">🔒 A neutral ID will be auto-generated. The code will be shown ONCE only.</p>
                <div className="form-group">
                  <label>User Type</label>
                  <select value={newCode.userType} onChange={(e) => setNewCode({ ...newCode, userType: e.target.value })}>
                    <option value="new_member">New Member</option>
                    <option value="existing_client">Existing Client</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Custom Code (optional)</label>
                  <input type="text" value={newCode.customCode} onChange={(e) => setNewCode({ ...newCode, customCode: e.target.value })} placeholder="Leave empty for auto-generated" />
                </div>
                <div className="modal-actions">
                  <button onClick={() => setShowCreateModal(false)} className="btn-secondary">Cancel</button>
                  <button onClick={handleCreateCode} className="btn-primary">Generate Code</button>
                </div>
              </>
            ) : (
              <>
                <h2 className="modal-title">Code Created</h2>
                <p className="modal-warning">⚠️ SAVE THIS CODE NOW. It cannot be retrieved later.</p>
                <div className="generated-code-info">
                  <div className="info-row"><span className="info-label">Neutral ID:</span><span className="info-value">{generatedCode.neutral_id}</span></div>
                </div>
                <div className="generated-code-display">
                  <code>{generatedCode.code}</code>
                  <button onClick={() => copyToClipboard(generatedCode.code)} className="copy-button">{copiedCode ? <Check size={16} /> : <Copy size={16} />}</button>
                </div>
                <button onClick={() => { setShowCreateModal(false); setGeneratedCode(null); }} className="btn-primary">I Have Saved The Code</button>
              </>
            )}
          </div>
        </div>
      )}

      {showIPRevealModal && (
        <div className="modal-overlay" onClick={closeIPRevealModal}>
          <div className="modal-content security-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title"><Shield size={20} /> Reveal Full IP Address</h2>
            {!revealedIP ? (
              <>
                <p className="modal-security-note">🔐 This action will be logged for security audit.</p>
                <p className="modal-warning">Re-enter your admin key to confirm.</p>
                <div className="form-group">
                  <label>Admin Key</label>
                  <input type="password" value={adminKeyInput} onChange={(e) => setAdminKeyInput(e.target.value)} placeholder="Enter admin key" autoFocus />
                </div>
                {revealError && <p className="modal-error">{revealError}</p>}
                <div className="modal-actions">
                  <button onClick={closeIPRevealModal} className="btn-secondary">Cancel</button>
                  <button onClick={handleRevealIP} className="btn-primary btn-danger" disabled={!adminKeyInput}>Reveal IP</button>
                </div>
              </>
            ) : (
              <>
                <p className="modal-security-note">✓ IP revealed. This action has been logged.</p>
                <div className="revealed-ip-display">
                  <span className="revealed-ip-label">Full IP Address:</span>
                  <code className="revealed-ip">{revealedIP}</code>
                </div>
                <button onClick={closeIPRevealModal} className="btn-primary">Close</button>
              </>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};
